import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RelatedIncidents = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30days');

  const relatedIncidents = [
    {
      id: 'INC-2024-0156',
      type: 'Near Miss',
      location: 'Manufacturing Floor B',
      date: '2024-09-18',
      severity: 'Medium',
      status: 'Under Investigation',
      similarity: 85
    },
    {
      id: 'INC-2024-0142',
      type: 'Equipment Failure',
      location: 'Manufacturing Floor B',
      date: '2024-09-10',
      severity: 'High',
      status: 'Closed',
      similarity: 72
    },
    {
      id: 'INC-2024-0138',
      type: 'Personal Injury',
      location: 'Warehouse Section C',
      date: '2024-09-05',
      severity: 'Low',
      status: 'Closed',
      similarity: 68
    }
  ];

  const safetyTrends = [
    {
      category: 'Equipment Failures',
      thisMonth: 8,
      lastMonth: 12,
      trend: 'down',
      percentage: 33
    },
    {
      category: 'Near Misses',
      thisMonth: 15,
      lastMonth: 11,
      trend: 'up',
      percentage: 36
    },
    {
      category: 'Personal Injuries',
      thisMonth: 3,
      lastMonth: 5,
      trend: 'down',
      percentage: 40
    }
  ];

  const complianceStatus = [
    {
      area: 'Safety Training',
      status: 'compliant',
      percentage: 94,
      dueDate: '2024-10-15'
    },
    {
      area: 'Equipment Inspections',
      status: 'warning',
      percentage: 78,
      dueDate: '2024-09-25'
    },
    {
      area: 'Incident Reporting',
      status: 'compliant',
      percentage: 100,
      dueDate: '2024-12-31'
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'low': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'high': return 'text-error bg-error/10';
      case 'critical': return 'text-error bg-error/20';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'closed': return 'text-success bg-success/10';
      case 'under investigation': return 'text-warning bg-warning/10';
      case 'open': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case 'compliant': return 'text-success bg-success/10';
      case 'warning': return 'text-warning bg-warning/10';
      case 'critical': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 'TrendingUp' : 'TrendingDown';
  };

  const getTrendColor = (trend) => {
    return trend === 'up' ? 'text-error' : 'text-success';
  };

  return (
    <div className="space-y-6">
      {/* Related Incidents */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground flex items-center">
            <Icon name="Search" size={20} className="mr-2 text-primary" />
            Related Incidents
          </h3>
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e?.target?.value)}
            className="text-sm border border-border rounded-md px-3 py-1 bg-background text-foreground"
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
          </select>
        </div>

        <div className="space-y-3">
          {relatedIncidents?.map((incident) => (
            <div key={incident?.id} className="p-4 border border-border rounded-lg hover:bg-muted/30 transition-clinical">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <span className="font-mono text-sm text-primary">{incident?.id}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident?.severity)}`}>
                    {incident?.severity}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(incident?.status)}`}>
                    {incident?.status}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">
                    {incident?.similarity}% similar
                  </span>
                  <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${incident?.similarity}%` }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-foreground">{incident?.type}</p>
                  <p className="text-muted-foreground">{incident?.location}</p>
                </div>
                <span className="text-muted-foreground">{incident?.date}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
            View All Related Incidents
          </Button>
        </div>
      </div>
      {/* Safety Trends */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="BarChart3" size={20} className="mr-2 text-accent" />
          Safety Trends
        </h3>

        <div className="space-y-4">
          {safetyTrends?.map((trend, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name={getTrendIcon(trend?.trend)} size={16} className={getTrendColor(trend?.trend)} />
                <span className="font-medium text-foreground">{trend?.category}</span>
              </div>
              
              <div className="flex items-center space-x-4 text-sm">
                <div className="text-center">
                  <p className="font-medium text-foreground">{trend?.thisMonth}</p>
                  <p className="text-muted-foreground">This Month</p>
                </div>
                <div className="text-center">
                  <p className="font-medium text-muted-foreground">{trend?.lastMonth}</p>
                  <p className="text-muted-foreground">Last Month</p>
                </div>
                <div className={`text-center ${getTrendColor(trend?.trend)}`}>
                  <p className="font-medium">{trend?.percentage}%</p>
                  <p className="text-xs">{trend?.trend === 'up' ? 'Increase' : 'Decrease'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Compliance Status */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Shield" size={20} className="mr-2 text-success" />
          Compliance Status
        </h3>

        <div className="space-y-4">
          {complianceStatus?.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <span className={`w-3 h-3 rounded-full ${
                  item?.status === 'compliant' ? 'bg-success' :
                  item?.status === 'warning' ? 'bg-warning' : 'bg-error'
                }`} />
                <span className="font-medium text-foreground">{item?.area}</span>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">{item?.percentage}%</p>
                  <p className="text-xs text-muted-foreground">Complete</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Due: {item?.dueDate}</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getComplianceColor(item?.status)}`}>
                    {item?.status === 'compliant' ? 'Compliant' : 
                     item?.status === 'warning' ? 'Attention Needed' : 'Critical'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
            Generate Compliance Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RelatedIncidents;